var __typeError = (msg) => {
  throw TypeError(msg);
};
var __accessCheck = (obj, member, msg) => member.has(obj) || __typeError("Cannot " + msg);
var __privateGet = (obj, member, getter) => (__accessCheck(obj, member, "read from private field"), getter ? getter.call(obj) : member.get(obj));
var __privateAdd = (obj, member, value) => member.has(obj) ? __typeError("Cannot add the same private member more than once") : member instanceof WeakSet ? member.add(obj) : member.set(obj, value);
var __privateSet = (obj, member, value, setter) => (__accessCheck(obj, member, "write to private field"), setter ? setter.call(obj, value) : member.set(obj, value), value);
var __privateMethod = (obj, member, method) => (__accessCheck(obj, member, "access private method"), method);
var _OverflowList_instances, waitForStyles_fn, initialize_fn, _scheduled, _handleIntersection, _handleChange, moveItemsToDefaultSlot_fn, reset_fn, updateMinimumReached_fn, _reflowItems, observeChanges_fn, unobserveChanges_fn, _refs, _resizeObserver, _mutationObserver, _intersectionObserver;
import { ResizeNotifier } from "@theme/utilities";
import { DeclarativeShadowElement } from "@theme/component";
class OverflowMinimumEvent extends Event {
  /**
   * Creates a new OverflowMinimumEvent
   * @param {boolean} minimumReached - Whether the minimum number of visible items has been reached
   */
  constructor(minimumReached) {
    super("overflowMinimum", { bubbles: true });
    this.detail = {
      minimumReached
    };
  }
}
class OverflowList extends DeclarativeShadowElement {
  constructor() {
    super(...arguments);
    __privateAdd(this, _OverflowList_instances);
    __privateAdd(this, _scheduled, false);
    /**
     * @param {IntersectionObserverEntry[]} entries
     */
    __privateAdd(this, _handleIntersection, (entries) => {
      const entry = entries[0];
      if (entry == null ? void 0 : entry.isIntersecting) {
        __privateGet(this, _intersectionObserver).disconnect();
        setTimeout(() => {
          var _a;
          (_a = this.querySelector(':scope > template[shadowrootmode="open"]')) == null ? void 0 : _a.remove();
          __privateGet(this, _reflowItems).call(this, entry.boundingClientRect.height);
        }, 0);
      }
    });
    /**
     * @type {ResizeObserverCallback & MutationCallback}
     */
    __privateAdd(this, _handleChange, () => {
      if (__privateGet(this, _scheduled)) return;
      __privateSet(this, _scheduled, true);
      requestAnimationFrame(
        () => setTimeout(() => {
          __privateGet(this, _reflowItems).call(this);
          __privateSet(this, _scheduled, false);
        }, 0)
      );
    });
    /**
     * Reflow items based on available space within the list.
     * @param {number} [listHeight] Initial height of the list
     * @param {HTMLElement | null} [lastVisibleElement] Optional element to place in last visible position
     */
    __privateAdd(this, _reflowItems, (listHeight = 0, lastVisibleElement = null) => {
      const { defaultSlot, overflowSlot, moreSlot, list, placeholder } = __privateGet(this, _refs);
      __privateMethod(this, _OverflowList_instances, unobserveChanges_fn).call(this);
      __privateMethod(this, _OverflowList_instances, moveItemsToDefaultSlot_fn).call(this);
      const elements = defaultSlot.assignedElements();
      const lastElement = elements[elements.length - 1];
      if (!lastElement) {
        __privateMethod(this, _OverflowList_instances, observeChanges_fn).call(this);
        return;
      }
      let visibleElements = [];
      let overflowingElements = [];
      let placeholderWidth = 0;
      let hasOverflow = false;
      if (listHeight > 0) {
        list.style.setProperty("height", "".concat(listHeight, "px"));
      }
      list.style.setProperty("flex-wrap", "wrap");
      placeholder.hidden = true;
      moreSlot.style.setProperty("order", "-1");
      moreSlot.hidden = false;
      lastVisibleElement == null ? void 0 : lastVisibleElement.style.setProperty("order", "-1");
      const moreSlotRect = moreSlot.getBoundingClientRect();
      elements.forEach((element) => {
        const elementRect = element.getBoundingClientRect();
        if (elementRect.top > moreSlotRect.top) {
          if (!overflowingElements.length) {
            placeholderWidth = elementRect.width;
          }
          hasOverflow = true;
          overflowingElements.push(element);
        } else {
          visibleElements.push(element);
        }
      });
      if (hasOverflow) {
        moreSlot.style.removeProperty("order");
      }
      lastVisibleElement == null ? void 0 : lastVisibleElement.style.removeProperty("order");
      for (const element of elements) {
        const targetSlot = overflowingElements.includes(element) ? overflowSlot.name : defaultSlot.name;
        if (element.slot !== targetSlot) {
          element.slot = targetSlot;
        }
      }
      list.style.setProperty("counter-reset", "overflow-count ".concat(overflowingElements.length));
      this.style.setProperty("--overflow-count", "".concat(overflowingElements.length));
      moreSlot.hidden = !hasOverflow;
      if (hasOverflow) {
        placeholder.style.width = "".concat(placeholderWidth, "px");
        placeholder.hidden = false;
      }
      list.style.setProperty("overflow", "unset");
      hasOverflow && __privateMethod(this, _OverflowList_instances, updateMinimumReached_fn).call(this, visibleElements);
      __privateMethod(this, _OverflowList_instances, observeChanges_fn).call(this);
    });
    /**
     * @type {{
     *   defaultSlot: HTMLSlotElement;
     *   overflowSlot: HTMLSlotElement;
     *   moreSlot: HTMLSlotElement;
     *   overflow: HTMLElement;
     *   list: HTMLUListElement;
     *   placeholder: HTMLLIElement;
     * }}
     */
    __privateAdd(this, _refs);
    /**
     * @type {ResizeObserver}
     */
    __privateAdd(this, _resizeObserver, new ResizeNotifier(__privateGet(this, _handleChange)));
    /**
     * @type {MutationObserver}
     */
    __privateAdd(this, _mutationObserver, new MutationObserver(__privateGet(this, _handleChange)));
    __privateAdd(this, _intersectionObserver, new IntersectionObserver(__privateGet(this, _handleIntersection), {
      // Extend the root margin to around one more viewport of a typical mobile screen.
      rootMargin: "640px 360px 640px 360px"
    }));
  }
  static get observedAttributes() {
    return ["disabled", "minimum-items"];
  }
  /**
   * @param {string} name
   * @param {string} oldValue
   * @param {string} newValue
   */
  attributeChangedCallback(name, oldValue, newValue) {
    if (name === "disabled") {
      if (newValue === "true") {
        __privateMethod(this, _OverflowList_instances, reset_fn).call(this);
      } else {
        __privateGet(this, _reflowItems).call(this);
      }
    }
  }
  async connectedCallback() {
    super.connectedCallback();
    await __privateMethod(this, _OverflowList_instances, waitForStyles_fn).call(this);
    __privateMethod(this, _OverflowList_instances, initialize_fn).call(this);
  }
  disconnectedCallback() {
    __privateGet(this, _resizeObserver).disconnect();
    __privateGet(this, _mutationObserver).disconnect();
    __privateGet(this, _intersectionObserver).disconnect();
  }
  get schedule() {
    var _a, _b;
    return typeof ((_b = (_a = Theme == null ? void 0 : Theme.utilities) == null ? void 0 : _a.scheduler) == null ? void 0 : _b.schedule) === "function" ? Theme.utilities.scheduler.schedule : (
      /** @param {FrameRequestCallback} callback */
      (callback) => requestAnimationFrame(() => setTimeout(callback, 0))
    );
  }
  /**
   * Get the minimum number of items before changing the minimum-reached state
   * @returns {number | null}
   */
  get minimumItems() {
    const value = this.getAttribute("minimum-items");
    return value ? parseInt(value, 10) : null;
  }
  get overflowSlot() {
    const { overflowSlot } = __privateGet(this, _refs);
    return overflowSlot;
  }
  get defaultSlot() {
    const { defaultSlot } = __privateGet(this, _refs);
    return defaultSlot;
  }
  /**
   * Show all items in the list.
   */
  showAll() {
    const { placeholder } = __privateGet(this, _refs);
    placeholder.style.setProperty("width", "0");
    placeholder.style.setProperty("display", "none");
    this.setAttribute("disabled", "true");
  }
}
_OverflowList_instances = new WeakSet();
waitForStyles_fn = function() {
  var _a;
  const styles = (_a = this.shadowRoot) == null ? void 0 : _a.querySelector('link[rel="stylesheet"]');
  if (!styles || styles.sheet) {
    return Promise.resolve();
  }
  return new Promise((resolve) => {
    styles.addEventListener("load", resolve);
  });
};
/**
 * Initialize the element
 */
initialize_fn = function() {
  const { shadowRoot } = this;
  if (!shadowRoot) throw new Error("Missing shadow root");
  const defaultSlot = shadowRoot.querySelector("slot:not([name])");
  const overflowSlot = shadowRoot.querySelector('slot[name="overflow"]');
  const moreSlot = shadowRoot.querySelector('slot[name="more"]');
  const overflow = shadowRoot.querySelector('[part="overflow"]');
  const list = shadowRoot.querySelector('[part="list"]');
  const placeholder = shadowRoot.querySelector('[part="placeholder"]');
  if (!(defaultSlot instanceof HTMLSlotElement) || !(overflowSlot instanceof HTMLSlotElement) || !(moreSlot instanceof HTMLSlotElement) || !(overflow instanceof HTMLElement) || !(list instanceof HTMLUListElement) || !(placeholder instanceof HTMLLIElement)) {
    throw new Error("Invalid element types in <OverflowList />");
  }
  __privateSet(this, _refs, {
    defaultSlot,
    overflowSlot,
    moreSlot,
    overflow,
    list,
    placeholder
  });
  this.addEventListener(
    "reflow",
    /** @param {CustomEvent<{lastVisibleElement?: HTMLElement}>} event */
    (event) => {
      __privateGet(this, _reflowItems).call(this, 0, event.detail.lastVisibleElement);
    }
  );
  const elements = defaultSlot.assignedElements().filter((element) => !(element instanceof HTMLTemplateElement));
  const firstElement = elements[0];
  const lastElement = elements[elements.length - 1];
  if (firstElement) {
    __privateGet(this, _intersectionObserver).observe(firstElement);
  }
  if (lastElement && lastElement !== firstElement) {
    __privateGet(this, _intersectionObserver).observe(lastElement);
  }
};
_scheduled = new WeakMap();
_handleIntersection = new WeakMap();
_handleChange = new WeakMap();
/**
 * Move all items to the default slot.
 */
moveItemsToDefaultSlot_fn = function() {
  const { defaultSlot, overflowSlot } = __privateGet(this, _refs);
  for (const element of overflowSlot.assignedElements()) {
    if (element.slot !== defaultSlot.name) {
      element.slot = defaultSlot.name;
    }
  }
};
/**
 * Reset the list to its initial state and disconnect the observers.
 */
reset_fn = function() {
  const { list } = __privateGet(this, _refs);
  __privateMethod(this, _OverflowList_instances, unobserveChanges_fn).call(this);
  __privateMethod(this, _OverflowList_instances, moveItemsToDefaultSlot_fn).call(this);
  list.style.removeProperty("height");
  this.style.setProperty("--overflow-count", "0");
};
/**
 * Sets the minimum-reached attribute and dispatches a custom event based on visible elements count
 * @param {Element[]} visibleElements - The currently visible elements
 */
updateMinimumReached_fn = function(visibleElements) {
  if (this.minimumItems !== null) {
    const minimumReached = visibleElements.length < this.minimumItems;
    if (minimumReached) {
      this.setAttribute("minimum-reached", "");
    } else {
      this.removeAttribute("minimum-reached");
    }
    this.dispatchEvent(new OverflowMinimumEvent(minimumReached));
  }
};
_reflowItems = new WeakMap();
observeChanges_fn = function() {
  __privateGet(this, _resizeObserver).observe(this);
  __privateGet(this, _mutationObserver).observe(this, { childList: true });
};
unobserveChanges_fn = function() {
  __privateGet(this, _resizeObserver).disconnect();
  __privateGet(this, _mutationObserver).disconnect();
};
_refs = new WeakMap();
_resizeObserver = new WeakMap();
_mutationObserver = new WeakMap();
_intersectionObserver = new WeakMap();
if (!customElements.get("overflow-list")) {
  customElements.define("overflow-list", OverflowList);
}
export {
  OverflowList,
  OverflowMinimumEvent
};
